import { useEffect, useMemo, useState } from "react";

/**
 * ArchGallery — mini galerie immersive AUTOMATIQUE pour les fenêtres
 * (arches) de la page d'accueil.
 *
 * Comportement voulu par l'utilisateur :
 *   • ken-burns lent identique à l'existant, conservé (aucun changement visuel du zoom)
 *   • plusieurs photos se succèdent avec un fondu enchaîné long et doux
 *   • PAS de carrousel classique : ni flèches, ni points, ni swipe, ni rythme rapide
 *
 * Si `images` ne contient qu'une seule entrée, on rend directement l'image
 * (aucun mécanisme d'auto-rotation) — même comportement que l'ancien code.
 *
 * Props :
 *   images         : Array<string>   liste de chemins (statique ou basename uploadé)
 *   alt            : string          texte alternatif appliqué à toutes les images
 *   burnsClass     : string          "cr-kenburns" ou "cr-kenburns-alt"
 *   mediaUrl       : fn              helper depuis useContent() (cache-busting)
 *   holdMs         : number          durée d'affichage d'une image (défaut 8500 ms)
 *   fadeMs         : number          durée du fondu enchaîné (défaut 1500 ms)
 */
export default function ArchGallery({
  images,
  alt,
  burnsClass = "cr-kenburns",
  mediaUrl,
  holdMs = 8500,
  fadeMs = 1500,
  style = {},
}) {
  const list = useMemo(() => (Array.isArray(images) ? images.filter(Boolean) : []), [images]);
  const [index, setIndex] = useState(0);

  // Auto-cycle uniquement si au moins 2 images.
  useEffect(() => {
    if (list.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % list.length);
    }, holdMs + fadeMs);
    return () => window.clearInterval(id);
  }, [list.length, holdMs, fadeMs]);

  // 1 image → rendu simple (identique au comportement historique)
  if (list.length <= 1) {
    const src = list[0];
    if (!src) return null;
    return (
      <img
        src={mediaUrl ? mediaUrl(src) : src}
        alt={alt}
        loading="lazy"
        className={burnsClass}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          filter: "saturate(0.92) contrast(0.98)",
          ...style,
        }}
        data-testid="arch-gallery-single"
      />
    );
  }

  // Plusieurs images empilées en absolu + fondu enchaîné long
  return (
    <div className="absolute inset-0" data-testid="arch-gallery-multi">
      {list.map((src, i) => {
        const active = i === index;
        return (
          <img
            key={`${src}-${i}`}
            src={mediaUrl ? mediaUrl(src) : src}
            alt={alt}
            loading={i === 0 ? "eager" : "lazy"}
            className={burnsClass}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              opacity: active ? 1 : 0,
              transition: `opacity ${fadeMs}ms ease-in-out`,
              filter: "saturate(0.92) contrast(0.98)",
              ...style,
            }}
          />
        );
      })}
    </div>
  );
}
